import * as v from 'valibot';
import { authorSchema, faqSchema } from '../base/schema';
import { structuredContentDocumentSchema } from '../structuredContent/schema';
import { nulltoOptional } from '../utils/validation-utils';
import { allStates, MarketplaceProductTypeEnum } from './constants';
import {
  EntityPlacementCtaTypeEnum,
  MarketplaceBankingSubProductTypeEnum,
  MarketplaceEntityTypeEnum,
  MarketplaceStatusEnum,
  MonetizableEnum,
} from './enums';
import { type StateAbbreviation } from './types';

export const marketplaceProductTypeSchema = v.enum(MarketplaceProductTypeEnum);
export const entityPlacementCtaTypeSchema = v.enum(EntityPlacementCtaTypeEnum);
export const monetizableSchema = v.enum(MonetizableEnum);
export const entityTypeSchema = v.pipe(
  v.string(),
  v.transform(val => val.toLowerCase()),
  v.enum(MarketplaceEntityTypeEnum),
);

export const DriverSchema = v.object({
  id: v.string(),
  description: nulltoOptional(v.string()),
  label: v.string(),
  labelTooltip: nulltoOptional(v.string()),
  value: nulltoOptional(v.string()),
  valueTooltip: nulltoOptional(v.string()),
});

export const stateSchema = v.custom<StateAbbreviation>(val => {
  const response = v.safeParse(v.picklist(allStates), val);
  if (response.success) {
    return true;
  }
  throw new Error(`Unsupported state: ${val}`);
});

export const statesSchema = nulltoOptional(v.array(stateSchema));

export const recapItemSchema = v.object({
  recapDescription: nulltoOptional(v.string()),
  marketplaceEntity: v.object({
    id: v.string(),
    name: v.string(),
    displayName: nulltoOptional(v.string()),
    monetizable: nulltoOptional(monetizableSchema),
    reviewLink: nulltoOptional(v.string()),
    states: statesSchema,
  }),
  showEditorialPickAsProductCard: nulltoOptional(v.boolean()),
});

export const recapListSectionSchema = v.object({
  title: v.string(),
  recapItems: nulltoOptional(v.array(recapItemSchema)),
});

export const belowSectionsSchema = v.object({
  belowSections: nulltoOptional(structuredContentDocumentSchema),
  modifiedAtFormatted: v.string(),
  methodology: nulltoOptional(structuredContentDocumentSchema),
  ratingMethodology: nulltoOptional(structuredContentDocumentSchema),
  authors: v.array(authorSchema),
  faqs: nulltoOptional(v.array(faqSchema)),
  recapListSection: nulltoOptional(recapListSectionSchema),
});

export const bestOfAwardsSchema = v.array(
  v.object({
    year: v.number(),
    category: v.string(),
  }),
);

export const MonetizableEnumSchema = v.enum(MonetizableEnum);

export const imageSchema = v.object({
  alt: nulltoOptional(v.string()),
  height: nulltoOptional(v.number()),
  resizableImagePath: nulltoOptional(v.string()),
  source: nulltoOptional(v.string()),
  sourceLarge: nulltoOptional(v.string()),
  sourceMedium: nulltoOptional(v.string()),
  width: nulltoOptional(v.number()),
});

export const ctaSchema = v.object({
  link: v.string(),
  showIcon: v.boolean(),
  subtext: nulltoOptional(v.string()),
  text: v.string(),
  type: v.enum(EntityPlacementCtaTypeEnum),
});

const bankingOfferSchema = v.object({
  displayName: nulltoOptional(v.string()),
  apy: nulltoOptional(v.number()),
  apyAnnotation: nulltoOptional(v.string()),
  apyOverride: nulltoOptional(v.string()),
  apySubtext: nulltoOptional(v.string()),
  consBullets: nulltoOptional(v.array(v.string())),
  cdDepositTerm: nulltoOptional(v.number()),
  earlyWithdrawalPenalty: nulltoOptional(v.string()),
  reviewLink: nulltoOptional(v.pipe(v.string(), v.url())),
  starRating: nulltoOptional(v.number()),
  starRatingTooltip: nulltoOptional(v.string()),
  institution: v.object({
    id: v.string(),
    name: nulltoOptional(v.string()),
    displayName: nulltoOptional(v.string()),
    status: nulltoOptional(v.enum(MarketplaceStatusEnum)),
  }),
  product: v.object({
    id: v.string(),
    name: nulltoOptional(v.string()),
    displayName: nulltoOptional(v.string()),
    status: nulltoOptional(v.enum(MarketplaceStatusEnum)),
    institution: nulltoOptional(
      v.object({
        institutionType: nulltoOptional(v.string()),
        ratingBankExperience: nulltoOptional(v.number()),
        starRating: nulltoOptional(v.number()),
        status: nulltoOptional(v.enum(MarketplaceStatusEnum)),
      }),
    ),
  }),
  minimumBalance: nulltoOptional(v.number()),
  monetizable: MonetizableEnumSchema,
  federallyInsuredAnnotation: nulltoOptional(v.string()),
  prosBullets: nulltoOptional(v.array(v.string())),
  cta: nulltoOptional(ctaSchema),
  image: nulltoOptional(imageSchema),
  driverApy: DriverSchema,
  driverBonus: DriverSchema,
  driverApyWithoutOverride: nulltoOptional(DriverSchema),
  driverBonusWithoutOverride: nulltoOptional(DriverSchema),
  driverMinimumBalance: DriverSchema,
  driverMonthlyFee: DriverSchema,
  driverOverdraftStarRating: DriverSchema,
  subProductType: nulltoOptional(v.enum(MarketplaceBankingSubProductTypeEnum)),
  subProductTypeLabel: nulltoOptional(v.string()),
  states: statesSchema,
  displayPages: nulltoOptional(v.array(v.string())),
});

const bankingInstitutionSchema = v.object({
  displayName: nulltoOptional(v.string()),
  institutionType: nulltoOptional(v.string()),
  reviewLink: nulltoOptional(v.pipe(v.string(), v.url())),
  starRating: nulltoOptional(v.number()),
  starRatingTooltip: nulltoOptional(v.string()),
  offerForCta: nulltoOptional(
    v.object({
      applyLink: nulltoOptional(v.pipe(v.string(), v.url())),
      id: v.string(),
      federallyInsuredAnnotation: nulltoOptional(v.string()),
      monetizable: nulltoOptional(MonetizableEnumSchema),
      name: v.string(),
      minimumBalance: nulltoOptional(v.number()),
      driverMinimumBalance: nulltoOptional(DriverSchema),
    }),
  ),
  monetizable: MonetizableEnumSchema,
  cta: nulltoOptional(ctaSchema),
  image: imageSchema,
  ratingBankExperience: nulltoOptional(v.number()),
  ratingOverdraftFee: nulltoOptional(v.number()),
});

const sharedMarketplaceEntitySchema = v.object({
  entityType: entityTypeSchema,
  productType: v.string(),
  status: v.enum(MarketplaceStatusEnum),
  id: v.string(),
  name: v.string(),
});

export const baseBankingMarketplaceEntitySchema = v.object({
  ...sharedMarketplaceEntitySchema.entries,
  ...v.partial(bankingOfferSchema).entries,
  ...v.partial(bankingInstitutionSchema).entries,
});

export const bankingOfferMarketplaceEntitySchema = v.object({
  ...sharedMarketplaceEntitySchema.entries,
  ...bankingOfferSchema.entries,
});

export const bankingInstitutionMarketplaceEntitySchema = v.object({
  ...sharedMarketplaceEntitySchema.entries,
  ...bankingInstitutionSchema.entries,
});

export const bankingMarketplaceSearchResponseSchema = v.object({
  marketplaceSearch: v.object({
    entities: v.array(baseBankingMarketplaceEntitySchema),
  }),
});

export const baseMarketplaceEntitySchema = baseBankingMarketplaceEntitySchema;

export const marketplaceEntitySchemaWithCtaOverride = v.object({
  ...baseMarketplaceEntitySchema.entries,
  ctaOverride: nulltoOptional(ctaSchema),
});

export const marketplaceEntitySchemaWithDriversAndCtaOverride = v.object({
  ...marketplaceEntitySchemaWithCtaOverride.entries,
  drivers: v.fallback(v.array(DriverSchema), []),
});

// entityType and productType depend on the marketplace entity being set
// if no marketplace entity is set then these fields are optional.
// Content sometimes removes the marketplace entity to keep the content
// but wanting to remove the product from the page. In that case, those fields exist but
// we don't track that nuance here.
const marketplaceFieldRelatedSchema = v.object({
  entityType: nulltoOptional(v.string()),
  productType: nulltoOptional(v.string()),
  marketplaceEntity: nulltoOptional(v.string()),
});

export const marketplaceFieldRelatedValidator = v.pipe(
  marketplaceFieldRelatedSchema,
  v.custom((input: unknown) => {
    const data = v.safeParse(marketplaceFieldRelatedSchema, input);
    if (data.issues) {
      return false;
    }
    const { entityType, productType, marketplaceEntity } = data.output;
    if (!marketplaceEntity) {
      return true;
    }
    return !!entityType && !!productType && !!marketplaceEntity;
  }),
);
